import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Star, Upload, Loader2, Search, Camera, AlertCircle } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";

const MAX_PHOTOS = 4;
const MAX_BYTES = 5 * 1024 * 1024; // 5MB per photo before encoding
const MAX_HIGHLIGHT = 2000;

// Compress an image File → base64 data URL (max 1600px on long edge, JPEG q=0.85)
async function fileToDataUrl(file, errLarge, errRead) {
  return new Promise((resolve, reject) => {
    if (file.size > MAX_BYTES) return reject(new Error(errLarge));
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      try {
        const MAX = 1600;
        const ratio = Math.min(1, MAX / Math.max(img.width, img.height));
        const w = Math.round(img.width * ratio);
        const h = Math.round(img.height * ratio);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      } catch (e) { reject(e); }
    };
    img.onerror = () => reject(new Error(errRead));
    img.src = url;
  });
}

export default function NewStoryModal({ open, onClose, onCreated, presetCampsite = null }) {
  const { t } = useTranslation();
  const [campsite, setCampsite] = useState(presetCampsite || null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [date, setDate] = useState("");
  const [nights, setNights] = useState(2);
  const [rating, setRating] = useState(5);
  const [highlight, setHighlight] = useState("");
  const [photos, setPhotos] = useState([]); // [{ data_url, caption }]
  const [useForCampsite, setUseForCampsite] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) setCampsite(presetCampsite || null); }, [open, presetCampsite]);

  useEffect(() => {
    if (!open || campsite || !search.trim()) { setResults([]); return; }
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/campsites?limit=8`, {
          params: { search: search.trim() },
          signal: ctrl.signal,
        });
        const items = Array.isArray(data) ? data : (data.items || []);
        setResults(items.slice(0, 8));
      } catch {} finally { setSearching(false); }
    }, 250);
    return () => { ctrl.abort(); clearTimeout(t); };
  }, [search, campsite, open]);

  if (!open) return null;

  const onPhotoFiles = async (files) => {
    const incoming = Array.from(files).slice(0, MAX_PHOTOS - photos.length);
    for (const file of incoming) {
      try {
        const data_url = await fileToDataUrl(file, t("story.toastImageTooLarge"), t("story.toastCantRead"));
        setPhotos((prev) => [...prev, { data_url, caption: "" }]);
      } catch (e) {
        toast.error(e.message || t("story.toastCantRead"));
      }
    }
  };

  const removePhoto = (i) => setPhotos((p) => p.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!campsite) return toast.error(t("story.toastPickCampsite"));
    if (!date) return toast.error(t("story.toastPickDate"));
    if (!highlight.trim() || highlight.trim().length < 4) return toast.error(t("story.toastAddHighlight"));
    // iter188 — soft nudge: trips with photos get far more kudos/shares.
    // Ask once, but let users skip if they only want text.
    if (photos.length === 0) {
      const ok = window.confirm(
        "Posting without a photo?\n\nTrips with photos get around 5× more views on the wall. You can still post without one — or tap Cancel to add one now (you can also add it later from your trip card)."
      );
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      const payload = {
        campsite_id: campsite.campsite_id,
        date_visited: date,
        nights_stayed: Number(nights) || 1,
        rating: Number(rating) || 5,
        highlight: highlight.trim(),
        photos: photos.map((p) => ({ image_data_url: p.data_url, caption: p.caption || null })),
        use_for_campsite: !!useForCampsite && photos.length > 0,
      };
      const { data } = await api.post("/wall", payload);
      const sharedCount = (payload.use_for_campsite && payload.photos.length) ? payload.photos.length : 0;
      if (sharedCount > 0) {
        toast.success(`Trip posted · ${sharedCount} ${sharedCount === 1 ? "photo" : "photos"} sent to ${campsite?.name || "the campsite"} for review.`);
      } else {
        toast.success(t("story.toastPosted"));
      }
      onCreated?.(data.story);
      // Reset form
      setCampsite(presetCampsite || null);
      setSearch(""); setResults([]);
      setDate(""); setNights(2); setRating(5);
      setHighlight(""); setPhotos([]); setUseForCampsite(true);
      onClose?.();
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setSubmitting(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="new-story-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white w-full sm:max-w-2xl max-h-[95vh] overflow-y-auto border-2 hairline-strong shadow-[0_8px_0_0_#111]">
        <header className="sticky top-0 bg-white border-b hairline px-5 py-3 flex items-center justify-between z-10">
          <h2 className="font-display text-xl">{t("story.shareTrip")}</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-[#f5f5f5]" aria-label={t("story.close")} data-testid="new-story-close">
            <X className="w-4 h-4" strokeWidth={2} />
          </button>
        </header>

        <div className="px-5 py-4 space-y-5">
          {/* CAMPSITE PICKER */}
          <div>
            <label className="eyebrow block mb-1.5">{t("story.campsite")}</label>
            {campsite ? (
              <div className="flex items-center justify-between gap-2 border hairline px-3 py-2 bg-[#fff4ee]">
                <div>
                  <div className="font-display text-base leading-tight">{campsite.name}</div>
                  <div className="text-[11px] mono text-[#666]">{campsite.region}</div>
                </div>
                <button
                  onClick={() => setCampsite(null)}
                  className="text-xs mono underline text-[#ff4500]"
                  data-testid="new-story-change-site"
                >{t("story.changeSite")}</button>
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-center gap-2 border hairline px-3 py-2">
                  <Search className="w-4 h-4 text-[#888]" strokeWidth={1.75} />
                  <input
                    autoFocus
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("story.campsiteSearchPlaceholder")}
                    className="flex-1 bg-transparent outline-none text-sm"
                    data-testid="new-story-search"
                  />
                  {searching && <Loader2 className="w-4 h-4 animate-spin text-[#888]" />}
                </div>
                {results.length > 0 && (
                  <ul className="absolute left-0 right-0 mt-1 border hairline bg-white max-h-60 overflow-y-auto z-10 shadow-[0_4px_0_0_#111]">
                    {results.map((r) => (
                      <li key={r.campsite_id}>
                        <button
                          onClick={() => { setCampsite(r); setSearch(""); setResults([]); }}
                          className="w-full text-left px-3 py-2 hover:bg-[#fff4ee] border-b hairline last:border-b-0"
                          data-testid={`new-story-pick-${r.campsite_id}`}
                        >
                          <div className="font-display text-sm">{r.name}</div>
                          <div className="text-[11px] mono text-[#666]">{r.region}</div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* DATE + NIGHTS + RATING */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="eyebrow block mb-1.5">{t("story.dateVisited")}</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full border hairline px-3 py-2 text-sm mono"
                max={new Date().toISOString().slice(0, 10)}
                data-testid="new-story-date"
              />
            </div>
            <div>
              <label className="eyebrow block mb-1.5">{t("story.nights")}</label>
              <input
                type="number"
                min="1" max="60"
                value={nights}
                onChange={(e) => setNights(e.target.value)}
                className="w-full border hairline px-3 py-2 text-sm mono"
                data-testid="new-story-nights"
              />
            </div>
            <div>
              <label className="eyebrow block mb-1.5">{t("story.rating")}</label>
              <div className="flex items-center gap-1 h-[38px]">
                {[1, 2, 3, 4, 5].map((i) => (
                  <button
                    key={i}
                    onClick={() => setRating(i)}
                    className="p-0.5"
                    aria-label={`${i} stars`}
                    data-testid={`new-story-star-${i}`}
                  >
                    <Star className={`w-5 h-5 ${i <= rating ? "fill-[#ff4500] text-[#ff4500]" : "text-[#ccc]"}`} strokeWidth={1.5} />
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* HIGHLIGHT */}
          <div>
            <label className="eyebrow block mb-1.5">{t("story.highlight")}</label>
            <textarea
              value={highlight}
              onChange={(e) => setHighlight(e.target.value.slice(0, MAX_HIGHLIGHT))}
              placeholder={t("story.highlightPlaceholder")}
              rows={10}
              className="w-full border hairline px-3 py-2 text-sm leading-relaxed resize-y min-h-[180px]"
              data-testid="new-story-highlight"
            />
            <div className="text-[10px] mono text-[#888] text-right mt-1">{highlight.length}/{MAX_HIGHLIGHT}</div>
          </div>

          {/* PHOTOS */}
          <div>
            <label className="eyebrow block mb-1.5">{t("story.addPhotoHelp", { max: MAX_PHOTOS })}</label>
            <div className="grid grid-cols-4 gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative aspect-square border hairline overflow-hidden bg-[#f5f5f5]">
                  <img src={p.data_url} alt="" className="w-full h-full object-cover" />
                  <button
                    onClick={() => removePhoto(i)}
                    className="absolute top-1 right-1 bg-black/70 text-white p-1 hover:bg-black"
                    aria-label="Remove photo"
                    data-testid={`new-story-remove-photo-${i}`}
                  >
                    <X className="w-3 h-3" strokeWidth={2.5} />
                  </button>
                </div>
              ))}
              {photos.length < MAX_PHOTOS && (
                <label className="aspect-square border-2 border-dashed hairline-strong flex flex-col items-center justify-center gap-1 text-[11px] mono text-[#666] cursor-pointer hover:bg-[#fff4ee]" data-testid="new-story-add-photo">
                  <Upload className="w-5 h-5 text-[#ff4500]" strokeWidth={1.75} />
                  Add
                  <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => onPhotoFiles(e.target.files)} />
                </label>
              )}
            </div>
            {photos.length > 0 && (
              <div className="mt-3 border-2 border-[#ff4500] bg-[#fff4ee] p-3" data-testid="new-story-share-with-campsite">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useForCampsite}
                    onChange={(e) => setUseForCampsite(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#ff4500] flex-shrink-0"
                    data-testid="new-story-use-for-campsite"
                  />
                  <span className="flex-1">
                    <span className="flex items-center gap-1.5 mb-1">
                      <Camera className="w-3.5 h-3.5 text-[#ff4500]" strokeWidth={2} />
                      <strong className="text-sm">Also share these {photos.length === 1 ? "photo" : "photos"} with {campsite?.name || "the campsite"}</strong>
                    </span>
                    <span className="block text-[11px] mono leading-snug text-[#5a3520]">
                      We'll send {photos.length === 1 ? "it" : "them"} to our moderator queue and add {photos.length === 1 ? "it" : "them"} to the campsite's photo gallery after a quick review. Landscapes &amp; surroundings only — please don't include people.
                    </span>
                  </span>
                </label>
              </div>
            )}
            {photos.length === 0 && (
              <div className="mt-2 text-[11px] mono text-[#888] flex items-start gap-1.5">
                <Camera className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                <span>Photos are optional but bring the Wall to life. Landscapes &amp; campsite views work best.</span>
              </div>
            )}
          </div>
        </div>

        <footer className="sticky bottom-0 bg-white border-t hairline px-5 py-3 flex items-center justify-between gap-3">
          <button onClick={onClose} className="text-sm mono underline text-[#666]" data-testid="new-story-cancel">{t("common.cancel")}</button>
          <button
            onClick={submit}
            disabled={submitting || !campsite || !date || highlight.trim().length < 4}
            className="btn-primary text-xs px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="new-story-submit"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <AlertCircle className="w-3.5 h-3.5" strokeWidth={2} />}
            {submitting ? t("story.posting") : t("story.postBtn")}
          </button>
        </footer>
      </div>
    </div>
  );
}
